/**
 * names.js — Helpers de normalización y matching de nombres de personas
 * (operadores CCTV y guardias). Puros, sin dependencias de Firebase/React.
 *
 * Conceptos:
 *  - normalizeName: forma de PRESENTACIÓN (trim + espacios colapsados + Title Case).
 *  - nameKey: clave de MATCHING (lowercase, sin tildes, espacios colapsados) —
 *    dos escrituras distintas de la misma persona deben producir la misma key.
 *  - aliases: mapa { nameKey(variante): "Nombre Canónico" } guardado en
 *    Facilities/{id}.nameAliases.{operators|guards}.
 */

/**
 * Trim + colapsar espacios + Title Case por palabra, seguro con acentos/ñ
 * (\b\w es solo-ASCII y rompía "Cañete" → "CañEte"). También normaliza
 * entradas EN MAYÚSCULAS ("GUILLEMO RUIZ" → "Guillemo Ruiz").
 */
export function normalizeName(raw) {
  if (!raw) return "";
  return String(raw)
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

/** Clave de matching: lowercase + sin diacríticos + espacios colapsados. */
export function nameKey(raw) {
  if (!raw) return "";
  return String(raw)
    .trim()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Aplica el mapa de aliases a un nombre crudo.
 * Si la key del nombre está en el mapa → devuelve el canónico.
 * Si no → devuelve el nombre normalizado.
 */
export function applyAliases(raw, aliasMap) {
  const key = nameKey(raw);
  if (key && aliasMap && aliasMap[key]) return aliasMap[key];
  return normalizeName(raw);
}

/**
 * Resuelve el nombre definitivo a guardar:
 * 1. Aplica el mapa de aliases del recinto (nameAliases.operators|guards).
 * 2. Matchea key-insensitive contra la lista existente del recinto para
 *    reutilizar la variante ya registrada en vez de crear una nueva.
 *
 * @param {string} raw        — texto ingresado por el usuario
 * @param {object} [aliasMap] — nameAliases.operators o nameAliases.guards
 * @param {string[]} [list]   — lista existente (facility.operators o .guards)
 */
export function resolveName(raw, aliasMap, list) {
  const aliased = applyAliases(raw, aliasMap || {});
  const key = nameKey(aliased);
  const arr = Array.isArray(list) ? list : [];
  const existing = arr.find((n) => nameKey(n) === key);
  return existing || aliased;
}

/** Distancia de Levenshtein simple (iterativa, O(a*b)). */
export function editDistance(a, b) {
  a = a || ""; b = b || "";
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[n];
}

/**
 * Sugiere pares de posibles duplicados dentro de una lista de nombres.
 * Criterios (sobre nameKey): misma key, contención de una key en otra,
 * o distancia de edición ≤ 2 (umbral conservador).
 *
 * @returns {Array<{a: string, b: string, reason: string}>}
 */
export function suggestDuplicates(list) {
  const names = (list || []).filter(Boolean);
  const out = [];
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const ka = nameKey(names[i]);
      const kb = nameKey(names[j]);
      if (!ka || !kb) continue;
      let reason = null;
      const minLen = Math.min(ka.length, kb.length);
      if (ka === kb) reason = "misma escritura (mayúsculas/tildes)";
      // Contención solo con claves de 4+ caracteres (evita falsos positivos
      // tipo "G" contenido en cualquier nombre)
      else if (minLen >= 4 && (ka.includes(kb) || kb.includes(ka))) reason = "un nombre contiene al otro";
      else if (minLen >= 4 && editDistance(ka, kb) <= 2) reason = "escritura muy similar";
      if (reason) out.push({ a: names[i], b: names[j], reason });
    }
  }
  return out;
}
